/**
 * Bundle cart drawer support
 * ---------------------------------------------------------------------------
 * Renders bundles added by the Bundle Builder app (a separate Shopify app at
 * ~/Desktop/bundle-builder) as a single parent line in the cart drawer with its
 * bundled products nested underneath. See docs/bundle-builder-integration.md.
 *
 * The app tags every line it adds with these properties:
 *
 *   _bundle_item      "true"                  this line belongs to a bundle
 *   _bundle_name      e.g. "starter-bundle"   which bundle config
 *   _bundle_group_id  a UUID                  groups the lines of ONE bundle
 *   _bundle_source    "custom" | "prebuilt"   picks the bundle's drawer title
 *   _bundle_info      e.g. "5 piece"          reporting only, not read here
 *   _bundle_title     e.g. "Beach Pack"       pre-built only: the drawer title
 *                                             (the block's label, else its title)
 *   _bundle_image     a Shopify CDN URL       the drawer image (a custom bundle
 *                                             sends one only if the block has
 *                                             a cart icon uploaded)
 *
 * Responsibilities:
 *   - Group cart lines by `_bundle_group_id`.
 *   - Work out the tier percentage a group has earned, so the drawer can show
 *     the discounted prices.
 *   - Remove a whole bundle as a unit.
 *
 * It deliberately does NOT render anything; `layout/theme.liquid` owns the
 * drawer markup and calls into here. Same split as `free-gift.js`.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TIERS ARE CACHED
 * ---------------------------------------------------------------------------
 * The discount is applied by a Shopify Function that only runs AT CHECKOUT, so
 * `cart.js` reports full prices for bundle lines — there is nothing in the cart
 * object to read a percentage off. The percentage shown in the drawer has to be
 * recomputed here from the group quantity.
 *
 * The tier table lives on the builder block's `data-tier-*` attributes, which
 * are only in the DOM on pages where the block is rendered. The drawer opens
 * everywhere. So the tiers are captured whenever a block IS on the page and
 * cached in localStorage, keyed by bundle id; the drawer then reads the cache.
 * A shopper cannot get a bundle into their cart without visiting the builder,
 * so by the time a bundle exists the cache is warm.
 *
 * With no cached tiers the drawer still groups the lines correctly and simply
 * shows full prices with no percentage — wrong grouping would be a bug, a
 * missing "20% off" is only a missed opportunity.
 *
 * NOTE: this mirrors the Function's tier maths, it does not share it. The
 * authority at checkout is the app's `$app:function-configuration` metafield.
 * If a merchant edits tiers in the app but not in the Theme Editor block (or
 * vice versa) the drawer and the checkout will disagree.
 */
(function (window, document) {
  "use strict";

  var ITEM_PROPERTY = "_bundle_item";
  var ID_PROPERTY = "_bundle_name";
  var GROUP_PROPERTY = "_bundle_group_id";
  var SOURCE_PROPERTY = "_bundle_source";
  var TITLE_PROPERTY = "_bundle_title";
  var IMAGE_PROPERTY = "_bundle_image";

  var BLOCK_SELECTOR = ".bundle-builder[data-bundle-id]";
  var STORAGE_KEY = "goodr:bundle-tiers";

  // Which bundles the shopper has shut, by group id. Bundles start open, so the
  // products are visible without a click and nothing is hidden from someone who
  // did not choose to hide it; collapsing is there for when a long bundle gets
  // in the way.
  //
  // Held here rather than in the DOM because `renderCartData` rebuilds the
  // markup from scratch on every cart change — state in the DOM would reset
  // whenever an unrelated line's quantity changed. Not persisted across page
  // loads, so the drawer always opens in the same, predictable shape.
  var collapsed = {};

  /* ------------------------------------------------------------------ *
   * Cart line helpers
   * ------------------------------------------------------------------ */

  function isBundleLine(line) {
    return !!(
      line &&
      line.properties &&
      line.properties[ITEM_PROPERTY] === "true" &&
      line.properties[GROUP_PROPERTY]
    );
  }

  function groupIdFor(line) {
    return isBundleLine(line) ? line.properties[GROUP_PROPERTY] : null;
  }

  function bundleIdFor(line) {
    return (line && line.properties && line.properties[ID_PROPERTY]) || null;
  }

  /**
   * The name the drawer gives a bundle: a pre-built bundle's own title, or null
   * for a custom one (and for pre-built lines added before the app sent titles),
   * which the drawer shows as "Your Bundle".
   */
  function titleFor(line) {
    var properties = (line && line.properties) || {};
    if (properties[SOURCE_PROPERTY] !== "prebuilt") return null;
    var title = String(properties[TITLE_PROPERTY] || "").trim();
    return title || null;
  }

  /**
   * The picture the app sent for a bundle — a pre-built bundle's cart or card
   * image, or the block's custom bundle cart icon — or null, in which case the
   * drawer shows the OG mark. Only an absolute or protocol-relative URL is
   * accepted: the value is a line property, which anyone can set through
   * cart/add.js, so nothing like `javascript:` reaches an `src`.
   */
  function imageFor(line) {
    var properties = (line && line.properties) || {};
    var image = String(properties[IMAGE_PROPERTY] || "").trim();
    return /^(https:)?\/\//.test(image) ? image : null;
  }

  /* ------------------------------------------------------------------ *
   * Tiers
   * ------------------------------------------------------------------ */

  function readCache() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      // Private browsing, quota, or a corrupt value. Not worth failing over —
      // the drawer degrades to grouping without a percentage.
      return {};
    }
  }

  function writeCache(cache) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch (e) {
      /* see readCache */
    }
  }

  /**
   * Same shape and same validation the builder's own script applies to these
   * attributes: a tier needs a whole positive quantity and a percentage in
   * (0, 100], and tiers are read low quantity to high.
   */
  function tiersFromBlock(block) {
    return ["One", "Two", "Three"]
      .map(function (n) {
        return {
          quantity: Number(block.dataset["tier" + n + "Quantity"]),
          discount: Number(block.dataset["tier" + n + "Discount"]),
        };
      })
      .filter(function (tier) {
        return (
          Number.isInteger(tier.quantity) &&
          tier.quantity > 0 &&
          tier.discount > 0 &&
          tier.discount <= 100
        );
      })
      .sort(function (a, b) {
        return a.quantity - b.quantity;
      });
  }

  /**
   * Cache the tier table of every builder block on this page. Safe to call more
   * than once; a block that re-renders (Theme Editor) just overwrites its entry.
   */
  function captureTiers() {
    var blocks = document.querySelectorAll(BLOCK_SELECTOR);
    if (!blocks.length) return;

    var cache = readCache();
    var changed = false;

    Array.prototype.forEach.call(blocks, function (block) {
      var bundleId = block.dataset.bundleId;
      var tiers = tiersFromBlock(block);
      if (!bundleId || !tiers.length) return;
      cache[bundleId] = tiers;
      changed = true;
    });

    if (changed) writeCache(cache);
  }

  function tiersFor(bundleId) {
    var cache = readCache();
    var tiers = bundleId && cache[bundleId];
    return Array.isArray(tiers) ? tiers : [];
  }

  /**
   * The percentage a group of this size has earned, or 0. Highest tier whose
   * quantity the group meets — the same rule the Function applies.
   */
  function discountFor(bundleId, quantity) {
    var tiers = tiersFor(bundleId);
    var earned = 0;

    tiers.forEach(function (tier) {
      if (quantity >= tier.quantity) earned = tier.discount;
    });

    return earned;
  }

  /* ------------------------------------------------------------------ *
   * Grouping
   * ------------------------------------------------------------------ */

  /**
   * Turn a flat cart into the order the drawer renders in: ordinary lines stay
   * where they are, and each bundle collapses into one entry at the position of
   * its first line.
   *
   * Returns an array of:
   *   { type: 'line',   item, index }
   *   { type: 'bundle', groupId, bundleId, title, image, lines: [{item, index}],
   *     quantity, subtotal, discount, savings, discountedSubtotal }
   *
   * `index` is the line's position in `cart.items`, which is what the drawer's
   * existing `updateCartItem(index, quantity)` expects.
   */
  function build(cart) {
    var items = (cart && cart.items) || [];
    var entries = [];
    var byGroup = {};

    items.forEach(function (item, index) {
      var groupId = groupIdFor(item);

      if (!groupId) {
        entries.push({ type: "line", item: item, index: index });
        return;
      }

      var group = byGroup[groupId];

      if (!group) {
        group = {
          type: "bundle",
          groupId: groupId,
          bundleId: bundleIdFor(item),
          title: titleFor(item),
          image: imageFor(item),
          lines: [],
          quantity: 0,
          subtotal: 0,
          finalSubtotal: 0,
        };
        byGroup[groupId] = group;
        entries.push(group);
      }

      group.lines.push({ item: item, index: index });
      group.quantity += item.quantity || 0;
      group.subtotal += originalLinePrice(item);
      group.finalSubtotal += finalLinePrice(item);
    });

    entries.forEach(function (entry) {
      if (entry.type !== "bundle") return;

      // What the cart has ALREADY taken off these lines. A discount function
      // does reach the online store cart, so when the app's automatic discount
      // is live this is the real, authoritative saving and cart.total_price
      // already reflects it — nothing more may be subtracted from the total, or
      // the checkout button ends up short by one discount.
      var applied = entry.subtotal - entry.finalSubtotal;

      if (applied > 0) {
        entry.savings = applied;
        entry.discountedSubtotal = entry.finalSubtotal;
        entry.pending = 0;
        entry.discount = Math.round((applied / entry.subtotal) * 100);
      } else {
        // No discount in the cart: either the merchant has not activated the
        // app's automatic discount yet, or it declined this group. Fall back to
        // the mirrored tier table, and flag the saving as still owed so the
        // total is brought in line with the prices shown.
        entry.discount = discountFor(entry.bundleId, entry.quantity);
        entry.savings = Math.round((entry.subtotal * entry.discount) / 100);
        entry.discountedSubtotal = entry.subtotal - entry.savings;
        entry.pending = entry.savings;
      }

      // One ratio for the whole group, so every child line is discounted on the
      // same basis as the parent's subtotal and they still add up.
      entry.ratio =
        entry.subtotal > 0 ? entry.discountedSubtotal / entry.subtotal : 1;
    });

    return entries;
  }

  function originalLinePrice(item) {
    if (item.original_line_price != null) return item.original_line_price;
    return item.line_price || 0;
  }

  function finalLinePrice(item) {
    if (item.final_line_price != null) return item.final_line_price;
    if (item.line_price != null) return item.line_price;
    return originalLinePrice(item);
  }

  /**
   * Saving the drawer is showing that the cart total does NOT already account
   * for, so the checkout button and free shipping bar can be brought in line
   * with the prices on the lines. Zero once the real discount is applying.
   */
  function pendingSavings(cart) {
    return build(cart).reduce(function (sum, entry) {
      return entry.type === "bundle" ? sum + entry.pending : sum;
    }, 0);
  }

  /* ------------------------------------------------------------------ *
   * Collapse state
   * ------------------------------------------------------------------ */

  function isExpanded(groupId) {
    return !collapsed[groupId];
  }

  /** Flips one bundle open or shut and returns its new expanded state. */
  function toggleExpanded(groupId) {
    if (collapsed[groupId]) {
      delete collapsed[groupId];
    } else {
      collapsed[groupId] = true;
    }
    return isExpanded(groupId);
  }

  /* ------------------------------------------------------------------ *
   * Removal
   * ------------------------------------------------------------------ */

  /**
   * Remove every line of one bundle in a SINGLE request.
   *
   * This must not be a loop of `updateCartItem(index, 0)` calls: those take an
   * array position, and every removal shifts the positions of the lines after
   * it, so the second call would remove the wrong line. `cart/update.js` keyed
   * by line key has no such problem and is one round trip.
   */
  function removeGroup(cart, groupId) {
    var updates = {};
    var found = false;

    ((cart && cart.items) || []).forEach(function (item) {
      if (groupIdFor(item) !== groupId) return;
      updates[item.key] = 0;
      found = true;
    });

    if (!found) return Promise.resolve(null);

    var root = (window.Shopify && window.Shopify.routes.root) || "/";

    return fetch(root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Could not remove this bundle.");
        return response.json();
      })
      .catch(function (error) {
        console.error("[Bundle] Failed to remove bundle:", error);
        throw error;
      });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  window.GoodrBundles = {
    isBundleLine: isBundleLine,
    groupIdFor: groupIdFor,
    bundleIdFor: bundleIdFor,
    titleFor: titleFor,
    imageFor: imageFor,
    captureTiers: captureTiers,
    tiersFor: tiersFor,
    discountFor: discountFor,
    build: build,
    pendingSavings: pendingSavings,
    isExpanded: isExpanded,
    toggleExpanded: toggleExpanded,
    removeGroup: removeGroup,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", captureTiers);
  } else {
    captureTiers();
  }
})(window, document);
